import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import LanguageSwitchButton from "./buttons/lang-switch-button";
import MenuButton from "./buttons/menu-button";
import NavigationMenu, {
  LinkLikeProps,
  NavigationItem,
} from "./navigation-menu";
import MiniCVSection from "../mini-cv";
import RightSwitcher from "./buttons/right-toggle";
import SkillsChart, { SkillsChartSlider } from "../../common/skills-chart";
import { SupportedLanguages as Language } from "./buttons/lang-switch-button";
import { NavLink, useLocation } from "react-router-dom";

interface NavTopMenuWithLangProps {
  language?: Language;
  supportedLanguages?: Language[];
  brandName?: string;
  initialMenuOpen?: boolean;
  navItems?: NavigationItem[];
  LinkComponent?: React.ComponentType<LinkLikeProps>;
  onLanguageChange?: (lang: Language) => void;
  onOpenMenu?: (state: boolean) => void;
}

const NavTopMenuWithLang: React.FC<NavTopMenuWithLangProps> = ({
  language = "RU",
  supportedLanguages = ["RU", "EN"],
  brandName = "VOLOBUEV",
  initialMenuOpen = false,
  navItems = [{ label: "Main", path: "/" }],
  onLanguageChange,
  onOpenMenu,
  LinkComponent,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(initialMenuOpen);
  const [rightSwitcherState, setRightSwitcherState] = useState<
    "mini-cv" | "skills"
  >("mini-cv");

  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setIsMenuOpen(false);
      onOpenMenu?.(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, onOpenMenu]);

  useEffect(() => {
    setIsMenuOpen(initialMenuOpen);
  }, [initialMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        onOpenMenu?.(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen, onOpenMenu]);

  const toggleMenu = () => {
    const nextState = !isMenuOpen;
    setIsMenuOpen(nextState);
    onOpenMenu?.(nextState);
  };

  return (
    <div className="w-full flex justify-center p-4">
      <motion.div
        initial={false}
        animate={
          isMenuOpen
            ? { width: 600, height: "auto" }
            : { width: 320, height: 60 }
        }
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 23,
          duration: 0.4,
        }}
        className="rounded-[10px] border border-[#ddd] bg-white overflow-hidden"
      >
        <div className="flex flex-col gap-[10px] h-full">
          {/* Header */}
          <div className="flex gap-[30px] h-[60px] items-center">
            <LanguageSwitchButton
              currentLanguage={language}
              supportedLanguages={supportedLanguages}
              onLanguageChange={(lang) => onLanguageChange?.(lang)}
            />

            <div className="flex flex-1 h-full items-center justify-center">
              <p className="font-['Hind',sans-serif] font-bold text-[16px] text-black">
                <NavLink to="/">{brandName}</NavLink>
              </p>
            </div>

            <MenuButton onToggle={toggleMenu} isOpen={isMenuOpen} />
          </div>

          {/* {isMenuOpen && ( */}
          <>
            {/* Body */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="gap-[30px] flex flex-1 flex-col md:flex-row md:max-h-[250px]"
            >
              <div className="flex flex-1 flex-col px-[20px] md:pr-[0px]">
                <NavigationMenu
                  activePath="/"
                  navItems={navItems}
                  LinkComponent={LinkComponent}
                />
              </div>

              <div className="flex flex-1 flex-col justify-center px-[20px] md:pl-[0px] gap-[10px]">
                <div className="flex flex-col h-[210px] justify-center ">
                  {rightSwitcherState === "mini-cv" ? (
                    <MiniCVSection
                      cvData={[
                        {
                          id: "geropharm",
                          name: "ООО Герофарм",
                          period: "2015 - Present",
                          tech: [".NET", "SharePoint", "React"],
                          positions: [
                            {
                              id: "geropharm-head",
                              title: "Рук. отдела",
                              year: 2021,
                            },
                            {
                              id: "geropharm-team-lead",
                              title: "Teamlead",
                              year: 2018,
                            },
                            {
                              id: "geropharm-senior",
                              title: "Senior",
                              year: 2016,
                            },
                            {
                              id: "geropharm-developer",
                              title: "Middle",
                              year: 2015,
                            },
                          ],
                        },
                        {
                          id: "ittilan",
                          name: "ООО Иттилан",
                          period: "2012 - 2015",
                          tech: [".NET", "SharePoint"],
                          positions: [
                            {
                              id: "ittilan-senior",
                              title: "Senior",
                              year: 2014,
                            },
                            {
                              id: "ittilan-support-dev",
                              title: "Middle",
                              year: 2013,
                            },
                            {
                              id: "ittilan-junior-support",
                              title: "Junior",
                              year: 2012,
                            },
                          ],
                        },
                        {
                          id: "freelance",
                          name: "Фриланс",
                          period: "2007 - 2012",
                          tech: ["PHP", "jQuery"],
                          positions: [],
                          secondary: true,
                        },
                      ]}
                    />
                  ) : (
                    <SkillsChartSlider
                      slides={[
                        {
                          skills: [
                            { name: "C#", width: "100%" },
                            { name: "F#", width: "20%" },
                            { name: "ASP.NET", width: "95%" },
                            { name: "PowerShell", width: "85%" },
                            { name: "SharePoint", width: "75%" },
                            { name: "JavaScript", width: "90%" },
                            { name: "React", width: "88%" },
                            { name: "Vue", width: "70%" },
                          ],
                          headLabel: "Backend",
                        },
                        {
                          skills: [
                            { name: "C#", width: "100%" },
                            { name: "F#", width: "20%" },
                            { name: "ASP.NET", width: "95%" },
                            { name: "PowerShell", width: "85%" },
                            { name: "SharePoint", width: "75%" },
                            { name: "JavaScript", width: "90%" },
                            { name: "React", width: "88%" },
                            { name: "Vue", width: "70%" },
                          ],
                          headLabel: "Frontend",
                        },
                        {
                          skills: [
                            { name: "C#", width: "100%" },
                            { name: "F#", width: "20%" },
                            { name: "ASP.NET", width: "95%" },
                            { name: "PowerShell", width: "85%" },
                            { name: "SharePoint", width: "75%" },
                            { name: "JavaScript", width: "90%" },
                            { name: "React", width: "88%" },
                            { name: "Vue", width: "70%" },
                          ],
                          headLabel: "DevOps",
                        },
                        {
                          skills: [
                            { name: "C#", width: "100%" },
                            { name: "F#", width: "20%" },
                            { name: "ASP.NET", width: "95%" },
                            { name: "PowerShell", width: "85%" },
                            { name: "SharePoint", width: "75%" },
                            { name: "JavaScript", width: "90%" },
                            { name: "React", width: "88%" },
                            { name: "Vue", width: "70%" },
                          ],
                          headLabel: "Management",
                        },
                      ]}
                    />
                  )}
                </div>

                <RightSwitcher
                  SwitchState={rightSwitcherState}
                  onSwitching={(state) => setRightSwitcherState(state)}
                />
              </div>
            </motion.div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="gap-[10px] h-[60px] items-center flex"
            >
              <div className="flex flex-1 h-full items-center justify-center pl-[20px] md:pl-[0px]">
                <p className="font-['Hind',sans-serif] text-[12px] text-left text-gray-700">
                  Проектов: 25+
                </p>
              </div>

              <div className="flex-1 h-full items-center justify-center hidden md:flex">
                <p className="font-['Hind',sans-serif] text-[12px] text-center text-gray-700">
                  Подчиненных: <span>18</span> /{" "}
                  <span title="до 9 одномоментно">9</span>
                </p>
              </div>

              <div className="flex flex-1 h-full items-center justify-center pr-[20px] md:pr-[0px]">
                <p className="font-['Hind',sans-serif] text-[12px] text-right text-gray-700">
                  Стаж: 14 лет
                </p>
              </div>
            </motion.div>
          </>
          {/* )} */}
        </div>
      </motion.div>
    </div>
  );
};

export default NavTopMenuWithLang;
export type { Language };
